import { motion, useReducedMotion } from "framer-motion";
import { Headphones, MessageSquare, Sparkles, TrendingUp } from "lucide-react";

import { FloatingWords, LoopClockProvider, PracticeLoop } from "@lyder-talk/ui";

import { cn } from "@/lib/utils";
import { usePointerParallax } from "./use-pointer-parallax";
import { useTranslation } from "react-i18next";

/**
 * The animated half of the authentication screen.
 *
 * `/auth/*` is the one place in the app that is allowed to be loud: it is a
 * single-purpose, full-height surface with exactly one decision to make, so
 * the right panel carries the whole pitch while the form on the left stays
 * still and calm. It is a deliberate sibling of the "Focus" variant described
 * in the design system — immersive chrome is fine here because there is no
 * other job on screen.
 *
 * Structure, top to bottom:
 *   eyebrow · headline · one line of copy
 *   the practice loop (shared with the marketing site)
 *   four benefits in a 2×2 grid
 *
 * On `xl` the text column sits left and the word cards take a rail down the
 * right. At `lg` there is no room for a second column, so the rail drops and
 * the content centres — the aurora and the loop carry the panel alone.
 *
 * The loop card and the word rail are the *same components* the marketing
 * site renders, pulled in from the UI package. That is deliberate: a second
 * hand-rolled demo here would drift from the landing page within a week, and
 * the one thing a pitch must never do is show two different products.
 *
 * Background is three separate layers so they can move independently: an
 * aurora of blurred colour blobs, a fine grid for scale, and drifting dust.
 * All of it stops under `prefers-reduced-motion`.
 */

/** Icons are fixed; the copy under each one is translated, so they are paired
 *  by index rather than declared as literal objects. */
const BENEFIT_ICONS = [MessageSquare, Headphones, Sparkles, TrendingUp];

/** Deterministic layout for the dust motes — no Math.random, so the panel
 *  looks identical on every server and client render. */
const DUST = Array.from({ length: 14 }, (_, i) => ({
  left: `${(i * 37 + 11) % 96}%`,
  top: `${(i * 53 + 7) % 94}%`,
  delay: `${(i % 7) * 1.7}s`,
  duration: `${18 + (i % 5) * 6}s`,
  size: i % 3 === 0 ? 2 : 1,
}));

export const AuthShowcase = () => {
  const { t } = useTranslation();
  const reduceMotion = useReducedMotion();
  const { smoothX, smoothY, onPointerMove, reset } = usePointerParallax();

  return (
    <aside
      className="relative hidden w-1/2 overflow-hidden lg:block lg:h-screen xl:w-[54%]"
      onPointerMove={onPointerMove}
      onPointerLeave={reset}
    >
      <AuroraBackground reduceMotion={reduceMotion} />
      <GridOverlay />
      <Dust reduceMotion={reduceMotion} />
      <FloatingWords
        className="auth-word-rail absolute inset-y-0 right-0 z-0 h-full w-[184px] shrink-0 self-auto"
        smoothX={smoothX}
        smoothY={smoothY}
      />

      {/* Its own scroll container, capped at the viewport so the form pane and
          this one scroll independently. `overscroll-y-contain` stops a scroll
          that runs out of content here from chaining to the page.

          No `justify-center` on this box. Centring and auto margins are not
          interchangeable: auto margins only absorb *positive* free space, so
          once the column outgrows a short window the free space goes
          negative, the margins collapse to zero, and `justify-center` then
          centres the negative overflow — pushing the eyebrow above the scroll
          origin where no amount of scrolling can reach it. `my-auto` on the
          child centres when there is room and simply starts at the top when
          there is not. */}
      <div className="relative z-10 flex h-full flex-col overflow-y-auto overscroll-y-contain px-10 py-10 xl:px-14">
        <motion.div
          initial={{ opacity: 0, x: 24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="my-auto w-full lg:mx-auto lg:max-w-[520px] xl:mx-0 xl:max-w-[460px]"
        >
          <Eyebrow />

          <h2 className="mt-4 font-display text-[34px] font-semibold leading-[1.08] tracking-tight text-white xl:mt-5 xl:text-[46px]">
            {t("auth.forms.showcase.headlineLineOne", "Learn a language by")}
            <br />
            {/* The gradient spans the second line only, so each language can
                lead or trail it however its grammar needs. */}
            <span className="bg-gradient-to-r from-[hsl(217_91%_78%)] to-[hsl(38_92%_58%)] bg-clip-text text-transparent">
              {t("auth.forms.showcase.headlineLineTwo", "actually speaking it")}
            </span>
          </h2>

          <p className="mt-3.5 max-w-md text-[15px] leading-relaxed text-white/55">
            {t(
              "auth.forms.showcase.description",
              "Ten focused minutes a day. Real conversations, your own words, and a record of how far you've come.",
            )}
          </p>

          {/* The landing page's loop, not a lookalike: same component, same
              clock, same five stages. The sign-up screen and the marketing
              site now describe one product rather than two. */}
          <div className="mt-6 xl:mt-7">
            <LoopClockProvider>
              <PracticeLoop />
            </LoopClockProvider>
          </div>

          <BenefitGrid />
        </motion.div>
      </div>

      {/* A hairline seam: the split should read as a fold in one surface, not
          as the edge of a coloured panel. */}
      <div className="pointer-events-none absolute inset-y-0 left-0 z-20 w-px bg-gradient-to-b from-transparent via-white/15 to-transparent" />
    </aside>
  );
};

/* ── Background ─────────────────────────────────────────────── */

const AuroraBackground = ({ reduceMotion }: { reduceMotion: boolean | null }) => (
  <div className="absolute inset-0 overflow-hidden bg-[hsl(224_45%_7%)]">
    {/* Base wash — a slow diagonal so the panel is never one flat colour.
        In dark mode this has to carry the whole split on its own: the form
        side is `hsl(25 8% 9%)`, so without real chroma here the two halves
        of the screen read as one dark slab. */}
    <div className="absolute inset-0 bg-[radial-gradient(120%_90%_at_20%_0%,hsl(217_62%_22%)_0%,transparent_58%),radial-gradient(100%_80%_at_90%_100%,hsl(38_58%_17%)_0%,transparent_52%)]" />

    <div
      className={cn(
        "auth-breathe absolute -left-24 -top-24 h-[420px] w-[420px] rounded-full bg-primary/40 blur-[110px]",
        !reduceMotion && "animate-mesh-1",
      )}
    />
    <div
      className={cn(
        "auth-breathe absolute -bottom-32 -right-16 h-[480px] w-[480px] rounded-full bg-secondary/30 blur-[120px]",
        !reduceMotion && "animate-mesh-2",
        reduceMotion && "opacity-70",
      )}
    />
    <div
      className={cn(
        "absolute left-1/2 top-[42%] h-[300px] w-[300px] -translate-x-1/2 rounded-full bg-blue-400/20 blur-[100px]",
        !reduceMotion && "animate-mesh-3",
      )}
    />

    <div
      className={cn(
        "absolute left-[8%] top-[58%] h-[360px] w-[360px] rounded-full bg-amber-500/15 blur-[110px]",
        !reduceMotion && "animate-mesh-4",
      )}
    />

    {/* Vignette: keeps the eye on the headline and hides the blob edges. */}
    <div className="absolute inset-0 bg-[radial-gradient(90%_70%_at_45%_40%,transparent_0%,hsl(224_45%_7%/_0.5)_100%)]" />
  </div>
);

const GridOverlay = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 opacity-[0.35]"
    style={{
      backgroundImage:
        "linear-gradient(hsl(0 0% 100% / 0.045) 1px, transparent 1px), linear-gradient(90deg, hsl(0 0% 100% / 0.045) 1px, transparent 1px)",
      backgroundSize: "56px 56px",
      maskImage: "radial-gradient(70% 60% at 50% 40%, black 0%, transparent 100%)",
      WebkitMaskImage: "radial-gradient(70% 60% at 50% 40%, black 0%, transparent 100%)",
    }}
  />
);

const Dust = ({ reduceMotion }: { reduceMotion: boolean | null }) =>
  reduceMotion ? null : (
    <div aria-hidden="true" className="pointer-events-none absolute inset-0 overflow-hidden">
      {DUST.map((mote, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-white/25 animate-particle"
          style={{
            left: mote.left,
            top: mote.top,
            width: mote.size,
            height: mote.size,
            animationDelay: mote.delay,
            animationDuration: mote.duration,
          }}
        />
      ))}
    </div>
  );

/* ── Content pieces ─────────────────────────────────────────── */

const Eyebrow = () => {
  const { t } = useTranslation();
  return (
  <motion.div
    initial={{ opacity: 0, y: 10 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.5, delay: 0.15 }}
    className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.06] px-3 py-1 backdrop-blur-sm"
  >
    <Sparkles className="h-3.5 w-3.5 text-secondary" />
    <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white/70">
      {t("auth.forms.showcase.eyebrow", "Your AI speaking partner")}
    </span>
  </motion.div>
  );
};

const BenefitGrid = () => {
  const { t } = useTranslation();
  const benefits = (t("auth.forms.showcase.benefits", { returnObjects: true }) as string[]) ?? [];
  return (
  <motion.ul
    initial="hidden"
    animate="visible"
    variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.08, delayChildren: 0.25 } } }}
    className="mt-6 grid grid-cols-1 gap-x-6 gap-y-3.5 sm:grid-cols-2 xl:mt-7"
  >
    {benefits.map((text, index) => {
      const Icon = BENEFIT_ICONS[index];
      return (
      <motion.li
        key={text}
        variants={{
          hidden: { opacity: 0, y: 12 },
          visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.16, 1, 0.3, 1] } },
        }}
        className="group flex items-start gap-3"
      >
        <span className="relative mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/[0.06] text-white/70 transition-all duration-300 group-hover:border-primary/40 group-hover:bg-primary/15 group-hover:text-white">
          <Icon className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
        </span>
        <p className="pt-1 text-[13px] leading-snug text-white/55 transition-colors duration-300 group-hover:text-white/80">
          {text}
        </p>
      </motion.li>
      );
    })}
  </motion.ul>
  );
};

export default AuthShowcase;